/**
 * Notifications & Email — shared DTO contract between the API and the web client.
 *
 * The kind allowlist and template registry live here so the API (the security
 * boundary, which re-validates and renders server-side) and the web UI agree on
 * exactly what is expressible. Email bodies are rendered server-side only — the
 * client never receives raw template bodies or any SMTP configuration.
 */

// ----------------------------------------------------------------------------
// Notification kinds
// ----------------------------------------------------------------------------

export const NOTIFICATION_KINDS = [
  'generic',
  'invitation',
  'password_reset',
  'mention',
  'system',
  'workflow',
] as const

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number]

// ----------------------------------------------------------------------------
// Email templates
// ----------------------------------------------------------------------------

/**
 * Transactional email templates the server knows how to render. The set is
 * deliberately small and fixed; renderTemplate() rejects anything else.
 */
export const EMAIL_TEMPLATES = ['invitation', 'password_reset', 'generic_notification'] as const

export type EmailTemplate = (typeof EMAIL_TEMPLATES)[number]

export type EmailTransportKind = 'smtp' | 'log'
export type EmailStatus = 'queued' | 'sent' | 'failed' | 'logged'

// ----------------------------------------------------------------------------
// DTOs
// ----------------------------------------------------------------------------

export interface NotificationDto {
  id: string
  kind: NotificationKind
  title: string
  body: string | null
  /** Optional in-app deep link (a same-origin path). */
  link: string | null
  data: Record<string, unknown>
  /** ISO timestamp when read, or null if unread. */
  readAt: string | null
  createdAt: string
}

/** Response of GET /notifications/unread-count. */
export interface UnreadCountDto {
  unread: number
}

export interface EmailLogDto {
  id: string
  toEmail: string
  template: string
  subject: string
  transport: EmailTransportKind
  status: EmailStatus
  error: string | null
  sentAt: string | null
  createdAt: string
}

// ----------------------------------------------------------------------------
// notify() service input contract (server-side; documented here so other
// verticals calling notify() at integration share the exact shape)
// ----------------------------------------------------------------------------

/**
 * Arguments accepted by the server-side notify() service. `email`, when present,
 * additionally sends a transactional email via the pluggable transport (falling
 * back to the LOG transport when SMTP is unconfigured).
 */
export interface NotifyInput {
  /** The org the recipient belongs to (server-supplied; never client input). */
  orgId: string
  /** The recipient app_user id. */
  recipientUserId: string
  kind?: NotificationKind
  title: string
  body?: string | null
  link?: string | null
  data?: Record<string, unknown>
  /** When set, also dispatch a transactional email. */
  email?: {
    to: string
    template: EmailTemplate
    /** Variables interpolated into the template (subject + body). */
    vars?: Record<string, string>
  }
}

// ----------------------------------------------------------------------------
// UI helpers (labels are advisory; the API allowlist is authoritative)
// ----------------------------------------------------------------------------

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  generic: 'Notification',
  invitation: 'Invitation',
  password_reset: 'Password reset',
  mention: 'Mention',
  system: 'System',
  workflow: 'Workflow',
}
